export type UserRole = 'owner' | 'admin' | 'agent' | 'viewer'

export type Actions = 'view' | 'create' | 'update' | 'delete' | 'manage'
export type Subjects = 
  | 'Conversation' 
  | 'Team' 
  | 'User' 
  | 'Settings' 
  | 'AllConversations'
  | 'all'

/**
 * Simple ability checker without @casl/ability dependency
 */
export class Ability {
  private permissions: Map<string, boolean> = new Map()

  constructor(role: UserRole) {
    this.definePermissions(role)
  }

  private definePermissions(role: UserRole) {
    switch (role) {
      case 'owner':
        // Owner can do everything
        this.permissions.set('manage:all', true)
        this.permissions.set('view:AllConversations', true)
        this.permissions.set('manage:Conversation', true)
        this.permissions.set('manage:Team', true)
        this.permissions.set('manage:User', true)
        this.permissions.set('manage:Settings', true)
        break

      case 'admin':
        // Admin can manage most things
        this.permissions.set('manage:Conversation', true)
        this.permissions.set('manage:Team', true)
        this.permissions.set('view:User', true)
        this.permissions.set('create:User', true)
        this.permissions.set('update:User', true)
        this.permissions.set('view:Settings', true)
        this.permissions.set('view:AllConversations', true) // Can see all conversations
        break

      case 'agent':
        // Agent can only manage their own conversations
        this.permissions.set('view:Conversation', true)
        this.permissions.set('update:Conversation', true)
        this.permissions.set('view:Team', true)
        break

      case 'viewer':
        // Viewer can only read
        this.permissions.set('view:Conversation', true)
        this.permissions.set('view:Team', true)
        break
    }
  }

  can(action: Actions, subject: Subjects): boolean {
    // Check specific permission
    if (this.permissions.get(`${action}:${subject}`)) {
      return true
    }
    
    // Check manage:all (owner)
    if (this.permissions.get('manage:all')) {
      return true
    }
    
    // Check manage:subject
    if (action !== 'manage' && this.permissions.get(`manage:${subject}`)) {
      return true
    }
    
    return false
  }

  cannot(action: Actions, subject: Subjects): boolean {
    return !this.can(action, subject)
  }
}

/**
 * Define abilities based on user role
 */
export function defineAbilityFor(role: UserRole): Ability {
  return new Ability(role)
}

/**
 * Hook to check if user can perform an action
 */
export function useAbility(role: UserRole) {
  return defineAbilityFor(role)
}
